import { useHandler } from "@redotech/react-util/hook";
import XIcon from "@redotech/redo-icons/x-close.svg";
import {
  DoNothingStep,
  PostPurchaseSplitStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { StepType } from "@redotech/redo-model/advanced-flow/common";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ObjectId } from "bson";
import { memo, useMemo } from "react";
import { PartialFlowStep, PartialWithRequiredType } from "..";
import { FlowInProgress } from "../../flow-editor";
import { deleteElementAndAllChildren } from "../../utils/delete-element";
import {
  SplitVariantControls,
  SplitVariantControlsProps,
} from "../common/split-variant-controls";

interface PostPurchaseSplitEditInspectorProps {
  step: PartialWithRequiredType<PostPurchaseSplitStep>;
  steps: PartialFlowStep[];
  setStep: (step: PartialWithRequiredType<PostPurchaseSplitStep>) => void;
  onClose?: () => void;
  setAutomation?: React.Dispatch<React.SetStateAction<FlowInProgress | null>>;
}

export const PostPurchaseSplitEditInspector = memo(
  function PostPurchaseSplitEditInspector({
    step,
    setStep,
    onClose,
    setAutomation,
  }: PostPurchaseSplitEditInspectorProps) {
    const treatments = useMemo(() => {
      return step.treatments ?? [];
    }, [step.treatments]);

    const totalWeight = useMemo(() => {
      return treatments.reduce(
        (acc, treatment) => acc + (treatment.weight || 0),
        0,
      );
    }, [treatments]);

    const isWeightValid: SplitVariantControlsProps["isWeightValid"] =
      useMemo(() => {
        const firstTreatment = treatments[0];
        if (!firstTreatment) {
          return { isValid: false, errorMessage: "No variants found" };
        }

        const allTreatmentsHaveWeight = treatments.every(
          (treatment) =>
            treatment.weight && treatment.weight > 0 && treatment.weight <= 100,
        );
        if (!allTreatmentsHaveWeight) {
          return {
            isValid: false,
            errorMessage: "All variants must have a weight between 0% and 100%",
          };
        }

        const totalWeightIs100 = totalWeight === 100;
        if (totalWeightIs100) {
          return { isValid: true };
        }

        const allTreatmentsHaveSameWeight = treatments.every(
          (treatment) =>
            treatment.weight && treatment.weight === firstTreatment.weight,
        );
        if (allTreatmentsHaveSameWeight) {
          return { isValid: true };
        } else {
          return {
            isValid: false,
            errorMessage:
              "All variants must add up to 100% (or have the same weight)",
          };
        }
      }, [totalWeight, treatments]);

    const handleTreatmentNameChange = useHandler((id: string, name: string) => {
      const updatedTreatments = treatments.map((treatment) =>
        treatment.treatmentId === id ? { ...treatment, name } : treatment,
      );
      setStep({ ...step, treatments: updatedTreatments });
    });

    const handleAddTreatment = useHandler(() => {
      // Create a DO_NOTHING step so the new variant arm renders an End node
      // (matching segment/page edges) and the saved flow keeps the empty arm
      // as a legitimate "no post-purchase page" treatment.
      const doNothingStep: DoNothingStep = {
        id: new ObjectId().toString(),
        type: StepType.DO_NOTHING,
      };
      const newTreatment = {
        treatmentId: new ObjectId().toString(),
        name: "New variant",
        nextId: doNothingStep.id,
        weight: 0,
      };
      if (setAutomation) {
        setAutomation((prev) =>
          prev ? { ...prev, steps: [...prev.steps, doNothingStep] } : prev,
        );
      }
      setStep({ ...step, treatments: [...treatments, newTreatment] });
    });

    const handleRemoveTreatment = useHandler((id: string) => {
      const treatmentToRemove = treatments.find(
        (treatment) => treatment.treatmentId === id,
      );
      const updatedTreatments = treatments.filter(
        (treatment) => treatment.treatmentId !== id,
      );
      setStep({ ...step, treatments: updatedTreatments });

      if (setAutomation && treatmentToRemove?.nextId) {
        setAutomation((prevAutomation) => {
          if (!prevAutomation || !treatmentToRemove.nextId) {
            return prevAutomation;
          }

          const updatedSteps = deleteElementAndAllChildren(
            treatmentToRemove.nextId,
            prevAutomation.steps,
          );

          return { ...prevAutomation, steps: updatedSteps };
        });
      }
    });

    const handleTreatmentWeightChange = useHandler(
      (id: string, weightStr: string) => {
        const updatedTreatments = treatments.map((treatment) =>
          treatment.treatmentId === id
            ? { ...treatment, weight: Number(weightStr) }
            : treatment,
        );
        setStep({ ...step, treatments: updatedTreatments });
      },
    );

    const handleBalanceWeights = useHandler(() => {
      if (treatments && treatments.length > 0) {
        const equalWeight = Math.round(100 / treatments.length);

        const updatedTreatments = treatments.map((treatment) => ({
          ...treatment,
          weight: equalWeight,
        }));

        setStep({ ...step, treatments: updatedTreatments });
      }
    });

    return (
      <Flex dir="column" gap="3xl" justify="flex-start">
        <Flex dir="row" justify="space-between">
          <Text fontSize="md" fontWeight="semibold">
            A/B test
          </Text>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size="sm"
          />
        </Flex>
        <Flex dir="column" gap="xl" justify="flex-start">
          <RedoTextInput
            fullWidth
            label="Test name"
            setValue={(value) => setStep({ ...step, name: value })}
            size="sm"
            value={step.name ?? ""}
          />
          <RedoTextareaInputField
            autoExpand
            fullWidth
            label="Hypothesis"
            setValue={(value) => setStep({ ...step, description: value })}
            value={step.description ?? ""}
          />
          <Flex dir="column" gap="xs" justify="flex-start">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Variants
            </Text>
            <SplitVariantControls
              isWeightValid={isWeightValid}
              onAddVariant={handleAddTreatment}
              onBalanceWeights={handleBalanceWeights}
              onRemoveVariant={handleRemoveTreatment}
              onVariantNameChange={handleTreatmentNameChange}
              onVariantWeightChange={handleTreatmentWeightChange}
              totalWeight={totalWeight}
              variants={treatments.map((treatment) => ({
                id: treatment.treatmentId ?? "",
                name: treatment.name ?? "",
                weight: treatment.weight ?? 0,
              }))}
            />
          </Flex>
        </Flex>
      </Flex>
    );
  },
);
